/**
 * Hook for managing email connections (OAuth-connected accounts).
 * Used for white-glove onboarding email scanning feature.
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export type EmailConnectionProvider = 'gmail' | 'outlook';

export type EmailConnectionStatus =
  | 'pending_auth'
  | 'auth_exchanging'
  | 'active'
  | 'paused'
  | 'error'
  | 'revoked'
  | 'pending_invite'
  | 'reauth_required'
  | 'admin_consent_required'
  | 'auth_failed';

export interface EmailConnection {
  connectionId: string;
  type: EmailConnectionProvider;
  userEmail: string;
  userId: string;
  status: EmailConnectionStatus;
  lastSyncAt?: string;
  lastSyncResult?: 'success' | 'partial' | 'failed';
  /** Reason the last sync ended non-clean; null when clean. */
  lastSyncError?: string | null;
  /** Reason the last OAuth exchange or refresh failed; null when clean. */
  lastAuthError?: string | null;
  /** Microsoft directory (tenant) id, present on Outlook connections. */
  providerTenantId?: string;
  messagesProcessed: number;
  filesExtracted: number;
  createdAt: string;
}

interface GetConnectionsResponse {
  connections: EmailConnection[];
  totalCount: number;
  availableProviders?: EmailConnectionProvider[];
  microsoftClientId?: string | null;
}

interface InitiateOAuthResponse {
  authUrl: string;
  connectionId: string;
}

interface RevokeConnectionResponse {
  success: boolean;
  message: string;
}

export interface InitiateOAuthOptions {
  /** Connection id being re-authorised; the backend keeps the existing record. */
  reconnectOf?: string;
}

interface UseEmailConnectionsReturn {
  connections: EmailConnection[];
  /** Providers the rollout gate permits for this company. */
  availableProviders: EmailConnectionProvider[];
  /** Entra application (client) id used to build the admin-consent URL. */
  microsoftClientId: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  initiateOAuth: (provider: EmailConnectionProvider, options?: InitiateOAuthOptions) => Promise<string>;
  revokeConnection: (connectionId: string) => Promise<void>;
  isConnecting: boolean;
  isRevoking: boolean;
}

/**
 * Hook to manage email connections for a company.
 * Provides functions to list, connect, and disconnect email accounts.
 */
export function useEmailConnections(companyId: string): UseEmailConnectionsReturn {
  const [connections, setConnections] = useState<EmailConnection[]>([]);
  const [availableProviders, setAvailableProviders] = useState<EmailConnectionProvider[]>(['gmail']);
  const [microsoftClientId, setMicrosoftClientId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  // Fetch connections
  const fetchConnections = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const getConnections = httpsCallable<{ companyId: string }, GetConnectionsResponse>(
        functions,
        'getConnections'
      );

      const result = await getConnections({ companyId });
      setConnections(result.data.connections);
      // Older backends do not return the gate; Gmail stays available either way.
      setAvailableProviders(
        Array.isArray(result.data.availableProviders) && result.data.availableProviders.length > 0
          ? result.data.availableProviders
          : ['gmail']
      );
      setMicrosoftClientId(result.data.microsoftClientId ?? null);
    } catch (err: any) {
      console.error('Failed to fetch email connections:', err);
      setError(err.message || 'Failed to load email connections');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Initial fetch
  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // Initiate OAuth flow
  const initiateOAuth = useCallback(async (
    provider: EmailConnectionProvider,
    options?: InitiateOAuthOptions
  ): Promise<string> => {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    setIsConnecting(true);
    try {
      const initiateOAuthFlow = httpsCallable<
        { companyId: string; provider: string; reconnectOf?: string },
        InitiateOAuthResponse
      >(functions, 'initiateOAuthFlow');

      // Omit reconnectOf entirely when absent: the callable transport turns
      // `undefined` into `null`, which the backend schema rejects.
      const result = await initiateOAuthFlow(
        options?.reconnectOf
          ? { companyId, provider, reconnectOf: options.reconnectOf }
          : { companyId, provider }
      );

      // Return the auth URL for the caller to redirect to
      return result.data.authUrl;
    } finally {
      setIsConnecting(false);
    }
  }, [companyId]);

  // Revoke connection
  const revokeConnection = useCallback(async (connectionId: string): Promise<void> => {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    setIsRevoking(true);
    try {
      const revoke = httpsCallable<
        { companyId: string; connectionId: string },
        RevokeConnectionResponse
      >(functions, 'revokeConnection');

      await revoke({ companyId, connectionId });

      // Refetch connections after revocation
      await fetchConnections();
    } finally {
      setIsRevoking(false);
    }
  }, [companyId, fetchConnections]);

  return {
    connections,
    availableProviders,
    microsoftClientId,
    loading,
    error,
    refetch: fetchConnections,
    initiateOAuth,
    revokeConnection,
    isConnecting,
    isRevoking,
  };
}
